import { FormControlLabel, Grid, MenuItem, Switch, TextField } from '@mui/material';
import CpfInput from 'components/masked-input/CpfInput';
import React, { useEffect, useRef } from 'react';
import { Patient } from 'types/patient';
import { PatientValidation } from '../validation/usePatientValidation';
import KeyboardDatePicker from 'components/pickers/KeyboardDatePicker';
import { asInputComponent } from 'components/masked-input/asInputComponent';

interface PatientFormProps {
  patient: Patient;
  handleChange: <K extends keyof Patient>(index: K, value: Patient[K]) => void;
  validation: PatientValidation;
  type?: 'edit' | 'new';
}

const PatientForm: React.FC<PatientFormProps> = ({ patient, handleChange, validation, type }) => {
  const inputs = {
    name: useRef<HTMLInputElement>(null),
    rg: useRef<HTMLInputElement>(null),
    document_number: useRef<HTMLInputElement>(null),
    email: useRef<HTMLInputElement>(null),
    person_type: useRef<HTMLSelectElement>(null),
  };

  useEffect(() => {
    const [key] = Object.keys(validation) as [keyof typeof inputs];

    if (!key) return;

    inputs[key].current?.focus();
  }, [validation]); // eslint-disable-line

  return (
    <Grid container size={12} spacing={2}>
      <Grid size={12}>
        <TextField
          fullWidth
          inputRef={inputs.name}
          label="Nome completo"
          value={patient.name}
          onChange={e => handleChange('name', e.target.value)}
          error={!!validation.name}
          helperText={validation.name}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          inputRef={inputs.document_number}
          error={!!validation.document_number}
          helperText={validation.document_number}
          label="CPF"
          value={patient.document_number}
          onChange={e => handleChange('document_number', e.target.value)}
          slotProps={{
            input: {
              inputComponent: asInputComponent(CpfInput),
            },
          }}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          inputRef={inputs.rg}
          error={!!validation.rg}
          helperText={validation.rg}
          label="RG"
          value={patient.rg}
          onChange={e => handleChange('rg', e.target.value)}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <KeyboardDatePicker
          fullWidth
          disableFuture
          label="Data de nascimento"
          value={patient.birth_date}
          onChange={date => handleChange('birth_date', date)}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          select
          fullWidth
          label="Sexo"
          value={patient.gender}
          onChange={e => handleChange('gender', e.target.value)}
        >
          <MenuItem value="M">Masculino</MenuItem>
          <MenuItem value="F">Feminino</MenuItem>
          <MenuItem value="O">Outro</MenuItem>
          <MenuItem value="N">Prefiro não dizer</MenuItem>
        </TextField>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          value={patient.marital_status}
          onChange={e => handleChange('marital_status', e.target.value)}
          select
          label="Estado civil"
        >
          <MenuItem value="S">Solteiro</MenuItem>
          <MenuItem value="C">Casado</MenuItem>
          <MenuItem value="D">Divorciado</MenuItem>
          <MenuItem value="V">Viúvo</MenuItem>
        </TextField>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          label="Profissão"
          value={patient.profession}
          onChange={e => handleChange('profession', e.target.value)}
        />
      </Grid>
      <Grid size={12}>
        <FormControlLabel
          label="Ativo"
          control={
            <Switch checked={!!patient.active} onChange={e => handleChange('active', e.target.checked ? 1 : 0)} />
          }
        />
      </Grid>
    </Grid>
  );
};

export default PatientForm;
